





































































































































































import { Component, Prop, Vue } from 'vue-property-decorator'
import { ReferralNode } from '@/api/model/referral-node'
import { ReferralFilterDirection, ReferralStatus, ReferralView } from '@/api/types'
import { User } from '@/api/model/user'
import { Branch } from '@/api/model/branch'
import { Contact } from '@/api/model/contact'
import { CompanyAPI, StatusReasonAPI } from '@/api/backend-client'
import { isEqual } from 'lodash'
import { namespace } from 'vuex-class'
import { generateChannelOptions, generateReferralStatusOptions, generateReferralStatusReasonOptions, generateYesNoOptions } from '@/helpers/options'
import { StatusReason } from '@/api/model/status-reason'

const referrals = namespace('referrals')

@Component
export default class Facets extends Vue {
    @Prop({ type: Boolean, default: false }) visible!: boolean
    @referrals.State readonly loading!: boolean
    @referrals.State readonly view!: string
    @referrals.State readonly direction!: string
    @referrals.State readonly priorityFilter!: number[]
    @referrals.State readonly typeFilter!: string[]
    @referrals.State readonly statusFilter!: string
    @referrals.State readonly statusReasonFilter!: string[]
    @referrals.State readonly newStatusReasonFilter!: StatusReason[]
    @referrals.State readonly referredByFilter!: User[]
    @referrals.State readonly referredByBranchFilter!: Branch[]
    @referrals.State readonly assignedToFilter!: User[]
    @referrals.State readonly assignedToBranchFilter!: Branch[]
    @referrals.State readonly channelFilter!: string[]
    @referrals.State readonly newSourceFilter!: string[]
    @referrals.State readonly scoreFilter!: number[]
    @referrals.Action applyView: ({ view: ReferralView, direction: ReferralFilterDirection, update: boolean }) => Promise<void>
    @referrals.Action applyPriorityFilter: ({ priorityFilter }) => Promise<void>
    @referrals.Action applyTypeFilter: ({ typeFilter }) => Promise<void>
    @referrals.Action applyStatusFilter: ({ statusFilter }) => Promise<void>
    @referrals.Action applyStatusReasonFilter: ({ statusReasonFilter }) => Promise<void>
    @referrals.Action applyNewStatusReasonFilter: ({ newStatusReasonFilter }) => Promise<void>
    @referrals.Action applyReferredByFilter: ({ referredByFilter, update }) => Promise<void>
    @referrals.Action applyReferredByBranchFilter: ({ referredByBranchFilter }) => Promise<void>
    @referrals.Action applyAssignedToFilter: ({ assignedToFilter, update }) => Promise<void>
    @referrals.Action applyAssignedToBranchFilter: ({ assignedToBranchFilter }) => Promise<void>
    @referrals.Action applyChannelFilter: ({ channelFilter }) => Promise<void>
    @referrals.Action applyNewSourceFilter: ({ newSourceFilter }) => Promise<void>
    @referrals.Action applyScoreFilter: ({ scoreFilter }) => Promise<void>
    @referrals.Action resetFilters: () => Promise<void>
    availableOutgoingBranches: Branch[] = []
    availableOutgoingUsers: User[] = []
    availableNewStatusReasons: StatusReason[] = []
    filters: any = {}

    async mounted(): Promise<void> {
        await this.loadOutgoingBranches()
        await this.loadOutgoingUsers()
        await this.loadNewStatusReasons()
    }

    async loadOutgoingBranches(): Promise<void> {
        // The referral nodes' branches and, if pipelines are enabled, the connections, in parallel
        const [outgoingBranches, connections] = await Promise.all([
            CompanyAPI.getOutgoingReferralBranches(),
            this.$currentCompany.pipelinesEnabled ? CompanyAPI.getOutgoingConnections() : Promise.resolve([]),
        ])
        const availableBranches = new Map<string, Branch>(outgoingBranches.map(branch => [branch.id, branch]))

        // A failed request gives no connections; a connection without a pipeline has no branches to add
        for (const connection of connections || []) {
            const pipeline = connection.pipeline
            if (!pipeline) {
                continue
            }

            // Add fallback branch
            if (pipeline.fallbackBranch) {
                availableBranches.set(pipeline.fallbackBranch.id, {
                    ...pipeline.fallbackBranch,
                    company: connection.targetCompany,
                } as Branch)
            }

            // Add pipeline branches
            (pipeline.branches || []).forEach(branch => {
                availableBranches.set(branch.id, {
                    ...branch,
                    company: connection.targetCompany,
                } as Branch)
            })
        }

        // Ensure own company teams are always added.
        for (const branch of this.$currentCompany.getAvailableBranches()) {
            availableBranches.set(branch.id, { ...branch, company: this.$currentCompany } as Branch)
        }

        this.availableOutgoingBranches = Array.from(availableBranches.values())
            .sort((a: Branch, b: Branch) => a.name.localeCompare(b.name))
    }

    async loadOutgoingUsers(): Promise<void> {
        const availableUsers = new Map<string, User>()

        this.availableBranches.forEach(branch => {
            branch.company.getAvailableUsers({ branch }).forEach(user => {
                availableUsers.set(user.id, user)
            })
        })

        this.availableOutgoingUsers = Array.from(availableUsers.values())
            .sort((a: User, b: User) => a.name.localeCompare(b.name))
    }

    async loadNewStatusReasons(): Promise<void> {
        this.availableNewStatusReasons = await StatusReasonAPI.getAll({
            company: this.$currentCompany.resourceIdentifier,
        })
    }

    onViewChanged(value: ReferralView): void {
        if (this.view === value) {
            return
        }

        this.applyView({
            view: value,
            direction: this.direction,
            update: true,
        })
    }

    onDirectionChanged(value: ReferralFilterDirection): void {
        if (this.direction === value) {
            return
        }

        this.applyView({
            view: this.view,
            direction: value,
            update: false,
        })

        // Reset branch filter of other direction
        if (value === 'in') {
            this.applyAssignedToBranchFilterType('all')
        } else if (value === 'out') {
            this.applyReferredByBranchFilterType('all')
        }
    }

    onPriorityChanged(value: number): void {
        this.applyPriorityFilter({
            priorityFilter: value !== null ? [value] : [],
        })
    }

    onTypeChanged(value: string[]): void {
        if (isEqual(this.typeFilter, value)) {
            return
        }

        this.applyTypeFilter({
            typeFilter: value,
        })
    }

    onStatusChanged(value: string[]): void {
        this.applyStatusFilter({
            statusFilter: value,
        })
    }

    onStatusReasonChanged(value: string[]): void {
        if (isEqual(this.statusReasonFilter, value)) {
            return
        }

        this.applyStatusReasonFilter({
            statusReasonFilter: value,
        })
    }

    onNewStatusReasonChanged(value: string[]): void {
        const mappedValue = [...value]
            .map(statusReasonId => this.availableNewStatusReasons.find(statusReason => statusReason.id === statusReasonId))

        if (isEqual(this.newStatusReasonFilter, mappedValue)) {
            return
        }

        this.applyNewStatusReasonFilter({
            newStatusReasonFilter: mappedValue,
        })
    }

    onReferredByFilterChanged(value: User[]): void {
        if (isEqual(this.referredByFilter, value)) {
            return
        }

        this.applyReferredByFilter({
            referredByFilter: value,
            update: true,
        })
    }

    onReferredByBranchFilterChanged(value: Branch[]): void {
        if (isEqual(this.referredByBranchFilter, value)) {
            return
        }

        this.applyReferredByBranchFilter({
            referredByBranchFilter: value,
        })
    }

    onAssignedToFilterChanged(value: User[]): void {
        if (isEqual(this.assignedToFilter, value)) {
            return
        }

        this.applyAssignedToFilter({
            assignedToFilter: value,
            update: true,
        })
    }

    onAssignedToBranchFilterChanged(value: Branch[]): void {
        if (isEqual(this.assignedToBranchFilter, value)) {
            return
        }

        this.applyAssignedToBranchFilter({
            assignedToBranchFilter: value,
        })
    }

    onChannelChanged(value: string[]): void {
        if (isEqual(this.channelFilter, value)) {
            return
        }

        this.applyChannelFilter({
            channelFilter: value,
        })
    }

    onNewSourceChanged(value: string[]): void {
        const mappedValue = value.map((newSourceId: any) => {
            if (typeof newSourceId === 'string' || newSourceId instanceof String) {
                return this.$currentCompany.activeSources.find(source => source.id === newSourceId)
            }

            return newSourceId
        })

        if (isEqual(this.newSourceFilter, mappedValue)) {
            return
        }

        this.applyNewSourceFilter({
            newSourceFilter: mappedValue,
        })
    }

    onScoreChanged(value: number): void {
        if (isEqual(this.scoreFilter, value)) {
            return
        }

        this.applyScoreFilter({
            scoreFilter: value !== null ? [value] : [],
        })
    }

    get availableBranches(): Branch[] {
        return this.availableOutgoingBranches
    }

    get availableUsers(): User[] {
        return this.$currentCompany.getAvailableUsers()
    }

    get directionOptions() {
        const view = this.view === 'user' ? 'my' : 'team'

        return [
            { label: this.$t(`navigation.leads_menu.${view}_referred`), value: 'in' },
            { label: this.$t(`navigation.leads_menu.${view}_created`), value: 'out' },
        ]
    }

    get viewOptions() {
        return [
            { label: this.$t('navigation.leads_menu.my_leads'), value: 'user' },
            { label: this.$t('navigation.leads_menu.team_leads'), value: 'branches' },
        ]
    }

    get priorityOptions() {
        return generateYesNoOptions()
    }

    get referralTypeOptions() {

        // Ensure referralNodes are loaded
        this.$currentCompany.ensureIncomingReferralsAreLoaded()
        this.$currentCompany.ensureOutgoingReferralsAreLoaded()

        const validReferralTypes = [...new Set([
            ...this.$currentCompany.incomingReferrals,
            ...this.$currentCompany.outgoingReferrals,
        ].map((node: ReferralNode) => node.type))]

        return validReferralTypes
            .map(type => ({
                label: this.$t(`referral.type.${type}`),
                value: type,
            }))
            .sort((a, b) => a.label.toString().localeCompare(b.label.toString()))
    }

    get referralStatusOptions() {
        return generateReferralStatusOptions()
    }

    get referralStatusReasonOptions() {
        return generateReferralStatusReasonOptions()
            .sort((a, b) => a.label.toString().localeCompare(b.label.toString()))
    }

    get newStatusReasonFilterValue() {
        return [...this.newStatusReasonFilter].map(statusReason => statusReason.id)
    }

    get referralNewStatusReasonOptions() {
        return this.availableNewStatusReasons
            .filter(statusReason => statusReason.active)
            .filter(statusReason => {
                if ([
                    ReferralStatus.SUCCESSFUL.toString(),
                    ReferralStatus.CLOSED.toString(),
                ].includes(this.statusFilter)) {
                    return this.statusFilter === statusReason.status
                } else {
                    return statusReason.status === ReferralStatus.OPEN
                }
            })
            .map(statusReason => {
                return {
                    label: statusReason.pipeline.name + ': ' + statusReason.name,
                    value: statusReason.id,
                }
            })
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    get channelOptions() {
        return generateChannelOptions()
    }

    get newSourceOptions() {
        const activeSources = this.$currentCompany.activeSources

        return activeSources
            .map(source => ({
                label: this.$t('referral.channel.' + source.channel).toString() + ': ' + source.name,
                value: source.id,
            }))
            .sort((a, b) => a.label.toString().localeCompare(b.label.toString()))
    }

    get scoreOptions() {
        return [
            { label: this.$t('score.low').toString(), value: -1 },
            { label: this.$t('score.neutral').toString(), value: 0 },
            { label: this.$t('score.high').toString(), value: 1 },
        ]
    }

    get ownBranches(): Branch[] {
        return this.$currentUser.getAvailableBranches()
    }

    get otherBranches(): Branch[] {
        return this.availableBranches
            .filter(branch => {
                return this.ownBranches.findIndex(b => b.id === branch.id) < 0
            })
    }

    get referredByBranchFilterTypeOptions(): {[key: string]: string} {
        return this.getBranchFilterTypeOptions('referredByBranch')
    }

    get referredByBranchFilterType(): string {
        return this.getBranchFilterType(this.referredByBranchFilter)
    }

    applyReferredByBranchFilterType(type: string): void {
        this.applyBranchFilterType(type, 'referredByBranchFilter', 'applyReferredByBranchFilter')
    }

    get assignedToBranchFilterTypeOptions(): {[key: string]: string} {
        return this.getBranchFilterTypeOptions('assignedToBranch')
    }

    get assignedToBranchFilterType(): string {
        return this.getBranchFilterType(this.assignedToBranchFilter)
    }

    applyAssignedToBranchFilterType(type: string): void {
        this.applyBranchFilterType(type, 'assignedToBranchFilter', 'applyAssignedToBranchFilter')
    }

    getBranchFilterTypeOptions(filterName: string): {[key: string]: string} {
        return {
            all: this.$t(`referral-list-filters.${filterName}Type.all`).toString(),
            own: this.$t(`referral-list-filters.${filterName}Type.own`).toString(),
            other: this.$t(`referral-list-filters.${filterName}Type.other`).toString(),
        }
    }

    getBranchFilterType(branches: Branch[]): string {
        const selectedOwnBranches = this.ownBranches.filter(ownBranch => {
            return branches.findIndex(branch => branch.id === ownBranch.id) >= 0
        })

        const selectedOtherBranches = this.otherBranches.filter(otherBranch => {
            return branches.findIndex(branch => branch.id === otherBranch.id) >= 0
        })

        if (selectedOwnBranches.length === this.ownBranches.length && selectedOtherBranches.length === 0) {
            return 'own'
        } else if (selectedOtherBranches.length === this.otherBranches.length && selectedOwnBranches.length === 0) {
            return 'other'
        } else {
            return 'all'
        }
    }

    applyBranchFilterType(type: string, filterName: string, applyFilterName: string): void {
        let branches = this[filterName]

        // Apply the selected type only if the type is different (prevents additional changes if the filter is changed directly)
        if (this.getBranchFilterType(branches) === type) {
            return
        }

        // Apply the selected type
        if (type === 'own') {
            branches = this.ownBranches
        } else if (type === 'other') {
            branches = this.otherBranches
        } else if (type === 'all') {
            branches = []
        }

        this[applyFilterName]({
            [filterName]: branches,
        })
    }
}
